import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * <farmazucca-chat-trigger> — bottone configurabile che apre il chatbot e fa
 * partire un messaggio come se l'avesse scritto il visitatore.
 *
 * Uso sul sito (rimpiazza i vecchi bottoni statici):
 *   <farmazucca-chat-trigger message="Vorrei prenotare la giornata MOC">
 *     Prenota la MOC
 *   </farmazucca-chat-trigger>
 *
 * - `message` (obbligatorio): il testo che parte in chat al click.
 * - etichetta del bottone = contenuto dell'elemento; se vuoto si usa `message`.
 * - `variant="outline"` per la versione con bordo; default pieno verde.
 * - restilizzabile dal sito via CSS part: farmazucca-chat-trigger::part(button)
 *
 * Se nella pagina manca <farmazucca-chat>, il trigger lo crea da solo: basta
 * quindi il solo script + i trigger per avere tutto funzionante.
 */
@customElement("farmazucca-chat-trigger")
export class FarmazuccaChatTrigger extends LitElement {
  @property() message = "";
  @property() variant: "solid" | "outline" = "solid";

  static styles = css`
    :host {
      display: inline-block;
      --fz-verde: #77b945;
      --fz-verde-scuro: #5e9b32;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        "Helvetica Neue", Arial, sans-serif;
    }
    button {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.65rem 1.25rem;
      border-radius: 999px;
      font: inherit;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;
    }
    button:hover { transform: translateY(-1px); }
    button:active { transform: translateY(0); }
    .solid {
      border: none;
      background: var(--fz-verde);
      color: #fff;
    }
    .solid:hover { background: var(--fz-verde-scuro); }
    .outline {
      border: 2px solid var(--fz-verde);
      background: transparent;
      color: var(--fz-verde-scuro);
    }
    .outline:hover { background: var(--fz-verde); color: #fff; }
    svg { width: 1.05rem; height: 1.05rem; flex-shrink: 0; }
  `;

  private attiva() {
    const msg = (this.message || "").trim();
    if (!msg) return;
    // Il widget della chat può non essere nella pagina: lo si aggiunge al volo,
    // così sul sito bastano lo script e i trigger. L'upgrade di un elemento già
    // definito è SINCRONO: dopo l'appendChild il listener della chat esiste già,
    // quindi si spedisce subito. (Niente requestAnimationFrame: con la pagina
    // occlusa non scatta mai e il click andrebbe perso — successo in collaudo.)
    if (!document.querySelector("farmazucca-chat")) {
      document.body.appendChild(document.createElement("farmazucca-chat"));
    }
    window.dispatchEvent(
      new CustomEvent("farmazucca-chat:send", { detail: { message: msg } }),
    );
  }

  render() {
    return html`
      <button part="button" class=${this.variant === "outline" ? "outline" : "solid"} @click=${this.attiva}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        <slot>${this.message}</slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "farmazucca-chat-trigger": FarmazuccaChatTrigger;
  }
}
